import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getReportsData } from "@/lib/operations.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/operator-shell";
import { HelpButton } from "@/components/kb/help-button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { KpiCard } from "@/components/reporting/kpi-card";
import { ChartCard } from "@/components/reporting/chart-card";
import { chartColors, axisProps, gridProps, tooltipStyle } from "@/components/reporting/chart-theme";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line,
} from "recharts";
import { format, eachDayOfInterval, startOfDay } from "date-fns";
import { ArrowDownToLine, ArrowUpFromLine, PackageSearch, Truck } from "lucide-react";
import { useReportsRange } from "@/hooks/use-reports-range";

export const Route = createFileRoute("/_authenticated/app/reports/")({ component: Reports });

function Reports() {
  const { tenantId } = useScope();
  const { sinceIso, untilIso, days: windowDays } = useReportsRange(14);
  const fetchReports = useServerFn(getReportsData);

  const { data: bundle } = useQuery({
    queryKey: ["reports", tenantId, sinceIso, untilIso],
    enabled: !!tenantId,
    queryFn: () => fetchReports({ data: { tenantId: tenantId!, sinceIso, toIso: untilIso } }),
  });
  const ledger = bundle?.ledger ?? [];
  const orders = bundle?.orders ?? [];

  // Build per-day buckets across the active window (cap to 90 buckets for the chart).
  const cappedDays = Math.min(windowDays, 90);
  const endDate = untilIso ? startOfDay(new Date(untilIso)) : startOfDay(new Date());
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (cappedDays - 1));
  const days = eachDayOfInterval({ start: startDate, end: endDate }).map((d) => ({
    date: d, key: format(d, "yyyy-MM-dd"), label: format(d, "MMM d"),
  }));

  const flow = days.map((d) => ({ date: d.label, received: 0, picked: 0 }));
  for (const r of ledger) {
    const k = format(new Date(r.created_at), "yyyy-MM-dd");
    const idx = days.findIndex((d) => d.key === k);
    if (idx < 0) continue;
    if (r.kind === "receipt") flow[idx].received += r.qty_delta ?? 0;
    if (r.kind === "pick") flow[idx].picked += Math.abs(r.qty_delta ?? 0);
  }

  const orderTrend = days.map((d) => ({ date: d.label, orders: 0 }));
  for (const o of orders) {
    const k = format(new Date(o.created_at), "yyyy-MM-dd");
    const idx = days.findIndex((d) => d.key === k);
    if (idx >= 0) orderTrend[idx].orders += 1;
  }

  const statusCounts: Record<string, number> = {};
  for (const o of orders) statusCounts[o.status] = (statusCounts[o.status] ?? 0) + 1;
  const statusData = Object.entries(statusCounts).map(([status, count]) => ({ status, count }));

  // KPIs across the window
  const kpis = useMemo(() => {
    let received = 0, picked = 0, shipped = 0;
    for (const r of ledger) {
      if (r.kind === "receipt") received += r.qty_delta ?? 0;
      if (r.kind === "pick") picked += Math.abs(r.qty_delta ?? 0);
      if (r.kind === "ship") shipped += Math.abs(r.qty_delta ?? 0);
    }
    return { received, picked, shipped, ordersCount: orders.length };
  }, [ledger, orders]);

  const topSkus = useMemo(() => {
    const agg = new Map<string, { code: string; name: string; units: number }>();
    for (const r of (bundle?.shipLedger ?? []) as Array<{
      sku_id: string;
      qty_delta: number | null;
      skus: { sku_code: string; name: string } | null;
    }>) {
      const entry = agg.get(r.sku_id) ?? { code: r.skus?.sku_code ?? "—", name: r.skus?.name ?? "", units: 0 };
      entry.units += Math.abs(r.qty_delta ?? 0);
      agg.set(r.sku_id, entry);
    }
    return Array.from(agg.values()).sort((a, b) => b.units - a.units).slice(0, 8);
  }, [bundle]);

  return (
    <>
      <PageHeader title="Executive Overview" description={`Activity over the selected window (${windowDays} day${windowDays === 1 ? "" : "s"}).`} actions={<HelpButton moduleKey="reports" />} />
      <div className="p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={<ArrowDownToLine className="h-4 w-4" />} label="Units received" value={kpis.received} />
          <KpiCard icon={<PackageSearch className="h-4 w-4" />} label="Units picked" value={kpis.picked} />
          <KpiCard icon={<ArrowUpFromLine className="h-4 w-4" />} label="Units shipped" value={kpis.shipped} />
          <KpiCard icon={<Truck className="h-4 w-4" />} label="Orders created" value={kpis.ordersCount} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Inbound vs Outbound" description="Units moved per day">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={flow}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" {...axisProps} />
                <YAxis {...axisProps} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="received" fill={chartColors.success} name="Received" radius={[3, 3, 0, 0]} />
                <Bar dataKey="picked" fill={chartColors.primary} name="Picked" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Orders Created per Day" description="Order volume trend">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={orderTrend}>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="date" {...axisProps} />
                <YAxis {...axisProps} allowDecimals={false} />
                <Tooltip {...tooltipStyle} />
                <Line type="monotone" dataKey="orders" stroke={chartColors.primary} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <ChartCard title="Orders by Status" height="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={statusData} layout="vertical">
              <CartesianGrid {...gridProps} horizontal={false} />
              <XAxis type="number" {...axisProps} allowDecimals={false} />
              <YAxis type="category" dataKey="status" {...axisProps} width={90} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="count" fill={chartColors.primary} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <Card>
          <CardHeader><CardTitle className="text-base">Top SKUs Shipped</CardTitle></CardHeader>
          <CardContent>
            {topSkus.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">No shipments in this window.</div>
            ) : (
              <div className="divide-y">
                {topSkus.map((s) => (
                  <div key={s.code} className="flex items-center justify-between py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-mono">{s.code}</div>
                      <div className="text-xs text-muted-foreground truncate">{s.name}</div>
                    </div>
                    <div className="tabular-nums font-medium">{s.units}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
